import type {
	PortalClientDto,
	PortalProfileDto,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTenant } from '../components/TenantGate';
import { setupCrossTabSync } from '../lib/portalTokenService';
import type { PortalLoginResponse } from '../services/portalAuthService';
import * as portalAuthService from '../services/portalAuthService';
import * as portalCartService from '../services/portalCartService';
import { useCartStore } from '../stores/cartStore';
import {
	restorePersistedSession,
	usePortalAuthStore,
} from '../stores/portalAuthStore';

/**
 * Fetch the current cart and update the cart store.
 * Called after login completes and on session restore so the
 * header badge and AddToCartControl states are accurate immediately.
 */
async function loadCart(token: string): Promise<void> {
	try {
		const response = await portalCartService.getCart(token);
		if (response.success && response.data != null) {
			useCartStore.getState().setCart(response.data);
		}
	} catch {
		// Cart fetch failed — non-critical, will retry when cart page opens
	}
}

interface UsePortalAuthReturn {
	token: string | null;
	profile: PortalProfileDto | null;
	/** Derived active client object (resolved from activeClientId + availableClients) */
	activeClient: PortalClientDto | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	availableClients: Array<PortalClientDto>;
	activeClientId: number | null;
	needsClientSelection: boolean;
	login: (email: string, password: string) => Promise<void>;
	requestSmsCode: (phoneNumber: string) => Promise<{ expiresIn: number }>;
	loginWithSms: (phoneNumber: string, code: string) => Promise<void>;
	selectClient: (clientId: number) => Promise<void>;
	logout: () => void;
	refreshSession: () => Promise<void>;
}

export function usePortalAuth(): UsePortalAuthReturn {
	const store = usePortalAuthStore();
	const { alias: tenantAlias } = useTenant();
	const { refreshTranslations } = useTranslation();
	const hasRestoredSession = useRef(false);

	// On mount: set up cross-tab sync and attempt session restore
	useEffect(() => {
		// Listen for auth changes from other tabs (token refresh, logout)
		const cleanupSync = setupCrossTabSync();

		// Attempt to restore a previous session from localStorage.
		// The store was seeded with isRestoringSession=true at module load
		// when a persisted token was present (see portalAuthStore.ts), so
		// route guards know to wait. We clear that flag here on every
		// terminal path — success, failure, or "no token to restore" —
		// so the guards can finally branch on the resolved auth state.
		if (!hasRestoredSession.current) {
			hasRestoredSession.current = true;

			const stored = restorePersistedSession();
			if (stored?.token != null && stored.refreshToken != null) {
				const state = usePortalAuthStore.getState();

				// Only restore if we're not already authenticated (avoids overwriting
				// state that was set by a login that just completed in this tab)
				if (!state.isAuthenticated && state.token == null) {
					state.setToken(stored.token);
					state.setRefreshToken(stored.refreshToken);
					state.setExpiresAt(stored.expiresAt);
					if (stored.languagePreference != null) {
						state.setLanguagePreference(stored.languagePreference);
					}
					state.setLoading(true);

					// Validate the stored token by fetching the profile.
					// If the token is expired, the apiClient's 401 retry will
					// attempt a refresh automatically.
					portalAuthService
						.me(stored.token)
						.then(async (meResult) => {
							// The server handles auto-selection for single-client users
							// in /auth/me, so activeClientId will only be null here if
							// the user has multiple clients and hasn't picked one yet.
							const s = usePortalAuthStore.getState();
							s.setProfile(meResult.profile);
							s.setAvailableClients(meResult.clients);
							s.setActiveClientId(meResult.activeClientId);
							s.setAuthenticated(true);
							s.setNeedsClientSelection(
								meResult.clients.length > 1 && meResult.activeClientId == null,
							);
							s.setDisplaySettings(meResult.displaySettings);

							// Update language from authoritative /me response
							if (meResult.languagePreference != null) {
								s.setLanguagePreference(meResult.languagePreference);
								s.persist();
							}

							s.setLoading(false);
							s.setRestoringSession(false);

							// Load translations after session restore
							await refreshTranslations();

							// Load cart if we have an active client
							const currentToken = usePortalAuthStore.getState().token;
							if (currentToken != null && meResult.activeClientId != null) {
								await loadCart(currentToken);
							}
						})
						.catch(() => {
							// Token and refresh both invalid — clear everything.
							// reset() sets state back to initialState which has
							// isRestoringSession=false, so guards unblock and
							// fall through to the unauthenticated redirect.
							usePortalAuthStore.getState().reset();
						});
				} else {
					// Another usePortalAuth instance from the same render pass
					// already adopted the persisted token and kicked off
					// /auth/me. Do NOT clear restoringSession here — that
					// instance is still waiting on the network round-trip.
					// Its .then() (or .catch via reset()) is the only thing
					// that knows when the gate can safely drop.
					//
					// The earlier version cleared the gate eagerly, assuming
					// "parallel instance has already finished". With ~30
					// usePortalAuth call sites mounting from the same
					// PortalLayout subtree on the very first render, the
					// second instance fires a microtask AFTER the first has
					// set the token but LONG before /auth/me lands. Clearing
					// here let PortalLayout drop its splash with
					// isAuthenticated still false, HomePage observed the
					// bootstrap's isAnonymousVisitor=true, and bounced
					// authenticated visitors to /catalog instead of
					// /dashboard on every refresh of `/`.
				}
			} else {
				// No persisted session to restore. The module-load peek may
				// have seen a token that was cleared between then and now
				// (e.g. devtools, another tab's logout) — clear the flag so
				// guards don't hang on a restore that will never happen.
				usePortalAuthStore.getState().setRestoringSession(false);
			}
		}

		return cleanupSync;
	}, []);

	/**
	 * Shared post-login flow: store tokens, fetch profile, handle
	 * client selection, and load cart. Used by both password login
	 * and SMS login so the post-auth behavior is identical.
	 */
	const handleLoginSuccess = useCallback(
		async (loginResult: PortalLoginResponse) => {
			const state = usePortalAuthStore.getState();
			const expiresAt = Date.now() + loginResult.expiresIn * 1000;

			state.setToken(loginResult.accessToken);
			state.setRefreshToken(loginResult.refreshToken);
			state.setExpiresAt(expiresAt);

			// Seed language preference from login response, then persist tokens + language
			state.setLanguagePreference(loginResult.languagePreference ?? 'en');
			state.persist();

			// Fetch profile + client list from crm-api.
			// The server auto-selects for single-client users in /auth/me,
			// so activeClientId will only be null if multi-client with no selection.
			const meResult = await portalAuthService.me(loginResult.accessToken);
			state.setProfile(meResult.profile);
			state.setAvailableClients(meResult.clients);
			state.setActiveClientId(meResult.activeClientId);
			state.setAuthenticated(true);
			state.setNeedsClientSelection(
				meResult.clients.length > 1 && meResult.activeClientId == null,
			);
			state.setDisplaySettings(meResult.displaySettings);

			// Update language preference from /me response (authoritative source)
			if (meResult.languagePreference != null) {
				state.setLanguagePreference(meResult.languagePreference);
				state.persist();
			}

			// Load translations now that we have auth + language preference
			await refreshTranslations();

			// Load cart if we have an active client
			if (meResult.activeClientId != null) {
				await loadCart(loginResult.accessToken);
			}
		},
		[refreshTranslations],
	);

	const login = useCallback(
		async (email: string, password: string) => {
			const state = usePortalAuthStore.getState();
			state.setLoading(true);
			try {
				// Brand/company is derived server-side from the request
				// hostname; the frontend doesn't pass anything brand-related.
				const loginResult = await portalAuthService.login(
					email,
					password,
					tenantAlias,
				);
				await handleLoginSuccess(loginResult);
			} catch (error: unknown) {
				usePortalAuthStore.getState().reset();
				throw error;
			} finally {
				usePortalAuthStore.getState().setLoading(false);
			}
		},
		[tenantAlias, handleLoginSuccess],
	);

	/**
	 * Request an SMS verification code for the given phone number.
	 * Returns the code expiry time so the UI can display a countdown.
	 */
	const requestSmsCode = useCallback(
		async (phoneNumber: string): Promise<{ expiresIn: number }> => {
			const result = await portalAuthService.requestSmsCode(
				phoneNumber,
				tenantAlias,
			);
			return { expiresIn: result.expiresIn };
		},
		[tenantAlias],
	);

	/**
	 * Verify an SMS code and complete authentication.
	 * The post-login flow (profile, client selection, cart) is identical
	 * to password-based login.
	 */
	const loginWithSms = useCallback(
		async (phoneNumber: string, code: string) => {
			const state = usePortalAuthStore.getState();
			state.setLoading(true);
			try {
				const loginResult = await portalAuthService.verifySmsCode(
					phoneNumber,
					code,
					tenantAlias,
				);
				await handleLoginSuccess(loginResult);
			} catch (error: unknown) {
				// Don't reset the auth store on verification failure — the user
				// was never authenticated, so there's nothing to clear. Resetting
				// here triggers a store update that cascades to other components
				// (e.g. UserMenu) during render, causing a React warning.
				throw error;
			} finally {
				usePortalAuthStore.getState().setLoading(false);
			}
		},
		[tenantAlias, handleLoginSuccess],
	);

	const selectClient = useCallback(async (clientId: number) => {
		const state = usePortalAuthStore.getState();
		const currentToken = state.token;
		if (currentToken == null) {
			throw new Error('Not authenticated');
		}

		state.setLoading(true);

		// Clear old client's cart immediately so stale data doesn't flash
		useCartStore.getState().setCart(null);

		try {
			const result = await portalAuthService.selectClient(
				currentToken,
				clientId,
			);
			state.setActiveClientId(result.activeClientId);
			state.setNeedsClientSelection(false);
			state.setAuthenticated(true);
			await loadCart(currentToken);
		} finally {
			usePortalAuthStore.getState().setLoading(false);
		}
	}, []);

	const logout = useCallback(() => {
		usePortalAuthStore.getState().reset();
	}, []);

	const refreshSession = useCallback(async () => {
		const state = usePortalAuthStore.getState();
		const currentToken = state.token;
		if (currentToken == null) {
			return;
		}

		try {
			const result = await portalAuthService.me(currentToken);
			state.setProfile(result.profile);
			state.setAvailableClients(result.clients);
			state.setActiveClientId(result.activeClientId);
			state.setDisplaySettings(result.displaySettings);
			state.setAuthenticated(true);
		} catch {
			// Token expired or invalid → force logout
			state.reset();
		}
	}, []);

	// Derive the full active client object from the ID + available clients list
	const activeClient = useMemo(
		() =>
			store.activeClientId != null
				? (store.availableClients.find((c) => c.id === store.activeClientId) ??
					null)
				: null,
		[store.activeClientId, store.availableClients],
	);

	return {
		token: store.token,
		profile: store.profile,
		activeClient,
		isAuthenticated: store.isAuthenticated,
		isLoading: store.isLoading,
		availableClients: store.availableClients,
		activeClientId: store.activeClientId,
		needsClientSelection: store.needsClientSelection,
		login,
		requestSmsCode,
		loginWithSms,
		selectClient,
		logout,
		refreshSession,
	};
}
